import '../styles/custom.css'

import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiButton,
  EuiSpacer,
  EuiPageBody,
  EuiPageContent,
  EuiPageContentHeader,
  EuiPageContentHeaderSection,
  EuiPageContentBody,
  EuiTitle,
  EuiIcon,
  EuiBreadcrumbs,
  EuiLoadingSpinner
} from '@elastic/eui'

import PageHeader from '../components/Header'
import Charts from '../components/Charts'
import KpiModal from '../components/KpiModal'
import DocumentFlyout from '../components/DocumentFlyout'

import { useModal } from '../hooks/useModal'
import { useFlyout } from '../hooks/useFlyout'
import { useKpiData } from '../hooks/useKpiData'

const user = JSON.parse(localStorage.getItem('user'))

/**
 * Composant principal Document - Refactorisé pour une meilleure lisibilité
 * 
 * Ce composant gère l'affichage des documents KPI avec leurs graphiques associés.
 * Il utilise des hooks personnalisés pour séparer les préoccupations :
 * - useKpiData : gestion des données KPI et de la logique métier
 * - useModal : gestion de l'état de la modal d'administration
 * - useFlyout : gestion de l'état du flyout de document
 * - useIsMounted : vérification du montage du composant
 */
function Document(props) {
  const {
    kpiCode,
    selectedItemName,
    selectedItemIcon,
    chartsOnly,
    breadcrumbs,
  } = props

  // Hooks personnalisés
  const { isModalVisible, showModal, closeModal } = useModal()
  const { 
    isFlyoutVisible, 
    size, 
    showFlyout, 
    closeFlyout 
  } = useFlyout()
  
  const {
    bodyHtml,
    projectCodes,
    kpiDocument,
    isLoaded,
    updateKpiField,
    submitKpiForm
  } = useKpiData(kpiCode)

  // Fonction pour gérer la soumission du formulaire modal
  const handleSubmitModal = () => {
    closeModal()
    submitKpiForm()
  }

  // Vérification des permissions pour afficher le bouton d'administration
  const canShowAdminButton = () => {
    return !chartsOnly &&
           user.role === 'ADMIN' &&
           projectCodes &&
           typeof projectCodes === 'object' &&
           projectCodes.length === 1 &&
           projectCodes[0] === kpiCode
  }

  // Vérification pour afficher le bouton document
  const canShowDocumentButton = () => {
    return !chartsOnly && 
           projectCodes && 
           typeof projectCodes === 'object'
  }

  return (
    <>
      {/* Modal d'administration des KPIs */}
      <KpiModal
        isVisible={isModalVisible}
        onClose={closeModal}
        kpiDocument={kpiDocument}
        onFieldChange={updateKpiField}
        onSubmit={handleSubmitModal}
      />

      {/* Flyout pour afficher le document */}
      <DocumentFlyout
        isVisible={isFlyoutVisible}
        onClose={closeFlyout}
        size={size}
        bodyHtml={bodyHtml}
      />

      <EuiPageBody component="div">
        <PageHeader />
        <EuiPageContent id="contentPage">
          <EuiPageContentHeader>
            <EuiPageContentHeaderSection>
              <EuiFlexGroup>
                {selectedItemIcon ? <EuiIcon type={selectedItemIcon} /> : null}
                <EuiTitle size="l">
                  <h2>{selectedItemName}</h2>
                </EuiTitle>
              </EuiFlexGroup>
              <EuiBreadcrumbs max={4} breadcrumbs={breadcrumbs} />
            </EuiPageContentHeaderSection>
            
            <EuiPageContentHeaderSection>
              <EuiFlexGroup
                key="editDocGroup"
                gutterSize="s"
                alignItems="center"
                responsive={false}
                wrap
              >
                {/* Bouton d'administration des KPIs */}
                {canShowAdminButton() && (
                  <EuiFlexItem grow={false}>
                    <EuiButton
                      style={{ position: 'relative', float: 'right' }}
                      id="buttonAdminKPIS"
                      onClick={showModal}
                    >
                      Administration KPIS
                    </EuiButton>
                  </EuiFlexItem>
                )}

                {/* Bouton d'affichage du document */}
                {canShowDocumentButton() && (
                  <EuiFlexItem grow={false}>
                    <EuiButton
                      style={{ position: 'relative', float: 'right' }}
                      id="buttonDocument"
                      onClick={showFlyout}
                    >
                      Document
                    </EuiButton>
                  </EuiFlexItem>
                )}
              </EuiFlexGroup>
            </EuiPageContentHeaderSection>
          </EuiPageContentHeader>
          
          <EuiPageContentBody>
            {/* Affichage des graphiques KPI */}
            <Charts kpiDocument={kpiDocument} />
            
            <EuiSpacer />
            
            {/* Indicateur de chargement */}
            {!isLoaded ? (
              <EuiFlexGroup justifyContent='center'>
                <EuiLoadingSpinner size='xl' />
              </EuiFlexGroup>
            ) : (
              /* Contenu du document */
              bodyHtml && !chartsOnly ? (
                <div
                  className='innerHtmlContent'
                  dangerouslySetInnerHTML={{
                    __html: bodyHtml.getElementById('Content').innerHTML,
                  }}
                />
              ) : (
                /* Code KPI si pas de contenu de document */
                kpiCode && !chartsOnly ? kpiCode : ''
              )
            )}
          </EuiPageContentBody>
        </EuiPageContent>
      </EuiPageBody>
    </>
  )
}

export default Document